import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/lib/auth";
import {
  LayoutDashboard, FileText, Settings, Receipt,
  Users, FileSpreadsheet, LogOut, Menu, X, Briefcase, Building2,
  Bell, BellOff, SlidersHorizontal, ListChecks, PanelLeftClose, PanelLeftOpen,
  Upload, Eye, Camera, Truck, AlertTriangle, CheckCircle2, Ruler, IndianRupee, Download, Palette, UserPlus, UserCheck, MessageSquareWarning, Boxes, Printer, Factory, Map, Banknote, FolderOpen, FileDown, HardHat, ShieldCheck, Activity,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import api from "@/shared/lib/api";
import { fmtISTNice } from "@/shared/lib/time";
import Logo from "@/shared/components/Logo";

// Simplified Project-Tracking sidebar (Feb 2026).
// 10 canonical roles: admin, management, csm, psm, ism, designer, client, prepress, production, accounts.
// Active modules: Projects, Clients, Delivery Challans, Invoices.
const STAFF_ROLES = ["admin", "management", "scope_user", "csm", "psm", "ism", "designer", "prepress", "production"];
const NAV = [
  { section: "WORKFLOW", items: [
    { to: "/dashboard",    label: "Dashboard",       icon: LayoutDashboard, roles: ["admin", "management", "scope_user", "csm", "psm", "ism"] },
    { to: "/my-designs",   label: "My Designs",      icon: Palette,         roles: ["designer"] },
    { to: "/my-preaudit",  label: "My Pre-Audit",    icon: Ruler,           roles: ["pre_audit"] },
    { to: "/sub-scope",    label: "Sub-scope",       icon: Boxes,           roles: ["admin", "management", "csm"] },
    { to: "/floorplan",    label: "Floorplan",       icon: Map,             roles: ["admin", "management", "csm", "psm", "pre_audit", "prepress", "designer", "ism", "site_inspector"] },
    { to: "/site-install", label: "Site Install",    icon: HardHat,         roles: ["admin", "management", "site_inspector"] },
    { to: "/dc-upload",    label: "Delivery Challans", icon: Truck,         roles: ["ism", "admin"] },
    { to: "/pocket-files", label: "Pocket Files",    icon: FolderOpen,      roles: ["admin", "management", "csm", "designer"] },
    { to: "/design-review", label: "Design Review",  icon: Eye,             roles: ["admin", "management", "csm"] },
    { to: "/print-designs", label: "Print Designs",  icon: Printer,         roles: ["admin", "management", "csm"] },
    { to: "/preaudit",     label: "Pre-Audit",       icon: Ruler,           roles: ["admin", "management", "psm"] },
    { to: "/prepress-board", label: "Pre-Press",     icon: Printer,         roles: ["admin", "management", "psm"] },
    { to: "/psm-procurement", label: "Procurement",  icon: Truck,           roles: ["admin", "management", "psm"] },
    { to: "/production",   label: "Production",      icon: Factory,         roles: ["admin", "management", "production"] },
    { to: "/prepress",     label: "My Pre-Press",    icon: Printer,         roles: ["prepress"] },
    { to: "/drafts",       label: "Project Drafts",  icon: FileSpreadsheet, roles: ["admin", "management", "scope_user"] },
    { to: "/scope-po",     label: "Purchase Orders", icon: IndianRupee,     roles: ["admin", "management", "scope_user"] },
    { to: "/projects",     label: "Projects",        icon: Briefcase,       roles: [...STAFF_ROLES, "site_inspector"] },
    { to: "/production-files", label: "Production Files", icon: FileDown,        roles: ["admin", "management", "scope_user"] },
    { to: "/clients",      label: "Clients",         icon: Building2,       roles: ["admin", "management", "scope_user"] },
  ]},
  { section: "ACCOUNTS", items: [
    { to: "/accounts-billing", label: "Billing", icon: Banknote, roles: ["admin", "management", "accounts"] },
    { to: "/accounts/commercials", label: "Commercials", icon: IndianRupee, roles: ["admin", "management", "scope_user", "accounts"] },
    { to: "/po-followup",  label: "PO Follow-up",        icon: IndianRupee,     roles: ["admin", "management", "sales"] },
    { to: "/invoices",     label: "All Invoices",        icon: Receipt,         roles: ["admin", "management", "accounts"] },
  ]},
  { section: "SETTINGS", items: [
    { to: "/masters", label: "Master Data", icon: Settings, roles: ["admin"] },
    { to: "/activity", label: "Activity", icon: Activity, roles: ["admin"] },
    { to: "/users",   label: "Users",       icon: Users,    roles: ["admin"] },
    { to: "/user-types", label: "User Types", icon: ShieldCheck, roles: ["admin"] },
  ]},
];

function canSee(role, allowed) {
  if (!role) return false;
  if (role === "admin") return true;
  return allowed.includes(role);
}

const ROLE_LABEL = {
  admin: "Administrator", management: "Business Management",
  scope_user: "Scope User",
  csm: "Client Success Manager",
  psm: "Project Site Manager",
  ism: "Implementation Success Manager",
  designer: "Designer", client: "Client",
  prepress: "Prepress Team", production: "Production Team",
  pre_audit: "Pre-Audit",
  accounts: "Accounts Team",
  sales: "Sales",
  site_inspector: "Site Inspector",
};

const TASK_ICON = {
  admin_create_client_login: UserCheck,
  csm_assign_designer: UserPlus,
  designer_share_concept: Palette,
  designer_final_upload: Upload, designer_rework: AlertTriangle,
  prepress_review_final: Eye, prepress_upload_production: Upload,
  prepress_rework_production: AlertTriangle,
  client_review_design: Eye, client_review_production: Eye,
  production_start: CheckCircle2, production_download_file: Download,
  csm_design_changes: AlertTriangle,
  csm_relay_feedback: MessageSquareWarning,
  psm_site_measure: Ruler, psm_install_photo: Camera,
  ism_sign_dc: FileText, ism_install_photo: Camera,
  accounts_bill: Truck, accounts_bill_outsourced: IndianRupee,
};
const TASK_DOT = {
  amber: "bg-amber-500", rose: "bg-rose-500", blue: "bg-blue-500",
  emerald: "bg-emerald-500", violet: "bg-violet-500",
};

function SidebarTasks({ collapsed, onNavigate }) {
  const [buckets, setBuckets] = useState([]);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  async function load() {
    try {
      const { data } = await api.get("/my-action-queue");
      setBuckets(data.buckets || []);
      setTotal(data.total || 0);
    } catch { /* silent */ }
  }
  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  function go(link) {
    onNavigate?.();
    navigate(link || "/dashboard");
  }

  if (collapsed) {
    return (
      <button onClick={() => go("/dashboard")}
        title={`${total} task${total === 1 ? "" : "s"} to do`}
        className="relative mx-auto my-2 h-11 w-11 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-700"
        data-testid="sidebar-tasks-collapsed">
        <ListChecks className="w-5 h-5" />
        {total > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center">{total}</span>
        )}
      </button>
    );
  }

  return (
    <div className="mb-2" data-testid="sidebar-tasks">
      <div className="px-5 py-2 flex items-center justify-between">
        <span className="text-[10px] font-mono tracking-widest text-zinc-400">MY TASKS</span>
        {total > 0 && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="sidebar-tasks-total">{total}</span>
        )}
      </div>
      {buckets.length === 0 ? (
        <div className="px-5 pb-2 flex items-center gap-2 text-xs text-emerald-600">
          <CheckCircle2 className="w-3.5 h-3.5" /> You're all clear
        </div>
      ) : (
        <div className="space-y-0.5">
          {buckets.map((b) => {
            const Icon = TASK_ICON[b.key] || ListChecks;
            return (
              <button key={b.key} onClick={() => go(b.items?.[0]?.link)}
                data-testid={`sidebar-task-${b.key}`}
                className="w-full flex items-center gap-2.5 pl-5 pr-3 py-2 text-left hover:bg-zinc-50 transition-colors group">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${TASK_DOT[b.tone] || "bg-zinc-400"}`} />
                <Icon className="w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-600 flex-shrink-0" />
                <span className="flex-1 text-[13px] text-zinc-700 truncate leading-tight">{b.label}</span>
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold flex items-center justify-center group-hover:bg-orange-100 group-hover:text-orange-700">{b.count}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sidebar_collapsed") === "1"; } catch { return false; }
  });

  function toggleCollapse() {
    setCollapsed((c) => {
      const nv = !c;
      try { localStorage.setItem("sidebar_collapsed", nv ? "1" : "0"); } catch { /* noop */ }
      return nv;
    });
  }

  async function onLogout() { await logout(); navigate("/login"); }

  const renderSidebar = (isCollapsed, onNav) => (
    <>
      <div className={`py-5 border-b border-zinc-200 bg-brand-surface flex items-center ${isCollapsed ? "justify-center px-2" : "px-5 justify-between"}`}>
        {isCollapsed ? <Logo size={32} showWordmark={false} /> : <Logo size={36} showWordmark={true} />}
      </div>
      <nav className="flex-1 py-2 overflow-y-auto overflow-x-hidden">
        {NAV.map((sec) => {
          const allowed = Array.isArray(user?.allowed_pages) ? user.allowed_pages : null;
          const visible = sec.items.filter((i) =>
            canSee(user?.role, i.roles)
            && (!allowed || user?.role === "admin" || i.to === "/pocket-files" || allowed.includes(i.to)));
          if (visible.length === 0) return null;
          return (
            <div key={sec.section} className="mb-2">
              {!isCollapsed && <div className="px-5 py-2 text-[10px] font-mono tracking-widest text-zinc-400">{sec.section}</div>}
              {visible.map((n) => (
                <NavLink key={n.to} to={n.to}
                  onClick={onNav}
                  title={isCollapsed ? n.label : undefined}
                  data-testid={`nav-${n.to.replace("/", "")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 h-11 text-sm transition-colors relative ${isCollapsed ? "justify-center px-2" : "px-5"} ${
                      isActive
                        ? "bg-orange-50 text-orange-700 font-semibold before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-orange-500"
                        : "text-zinc-700 hover:bg-zinc-50"
                    }`}>
                  <n.icon className="w-4 h-4 flex-shrink-0" />
                  {!isCollapsed && <span className="font-medium">{n.label}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}

        {/* Per-user tasks (mirrors the dashboard action queue) */}
        {user?.role !== "client" && (
          <>
            {!isCollapsed && <div className="border-t border-zinc-100 my-2" />}
            <SidebarTasks collapsed={isCollapsed} onNavigate={onNav} />
          </>
        )}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {onNav === undefined && (
        <button onClick={toggleCollapse} data-testid="sidebar-collapse-toggle"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className={`border-t border-zinc-200 h-10 flex items-center gap-2 text-xs text-zinc-500 hover:bg-zinc-50 transition-colors ${isCollapsed ? "justify-center" : "px-5"}`}>
          {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <><PanelLeftClose className="w-4 h-4" /> <span>Collapse</span></>}
        </button>
      )}

      <div className={`border-t border-zinc-200 bg-brand-surface ${isCollapsed ? "p-2" : "p-4 space-y-2"}`}>
        {isCollapsed ? (
          <button onClick={onLogout} data-testid="logout-button-collapsed" title="Sign Out"
            className="w-full h-10 flex items-center justify-center hover:bg-white border border-zinc-300 rounded">
            <LogOut className="w-4 h-4" />
          </button>
        ) : (
          <>
            <div className="text-[10px] font-mono text-orange-600 uppercase tracking-widest">{user?.user_type_name || ROLE_LABEL[user?.role] || user?.role}</div>
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
            <button onClick={onLogout} data-testid="logout-button"
              className="mt-2 w-full h-10 flex items-center justify-center gap-2 border border-zinc-300 hover:bg-white text-sm rounded transition-colors">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </>
        )}
      </div>
    </>
  );

  const sidebarW = collapsed ? "w-16" : "w-64";
  const mainMl = collapsed ? "lg:ml-16" : "lg:ml-64";

  return (
    <div className="min-h-screen flex bg-zinc-50">
      <aside className={`hidden lg:flex ${sidebarW} bg-white border-r border-zinc-200 flex-col fixed inset-y-0 left-0 transition-[width] duration-200`}>
        {renderSidebar(collapsed, undefined)}
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-white border-b border-zinc-200 flex items-center justify-between px-4">
        <Link to="/dashboard">
          <Logo size={28} showWordmark={true} />
        </Link>
        <button onClick={() => setMobileOpen(true)}
          className="h-10 w-10 flex items-center justify-center border border-zinc-300 rounded"
          data-testid="mobile-menu-button">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative bg-white w-72 flex flex-col">
            <button onClick={() => setMobileOpen(false)}
              className="absolute right-3 top-3 h-9 w-9 flex items-center justify-center hover:bg-zinc-100 z-10">
              <X className="w-5 h-5" />
            </button>
            {renderSidebar(false, () => setMobileOpen(false))}
          </aside>
        </div>
      )}

      <main className={`flex-1 ${mainMl} pt-14 lg:pt-0 min-w-0 relative transition-[margin] duration-200`}>
        <NotificationBell />
        <Outlet />
      </main>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const [prefs, setPrefs] = useState({ muted: [], available_categories: [] });
  const navigate = useNavigate();
  const ref = useRef(null);

  async function load() {
    try {
      const { data } = await api.get("/notifications");
      setData(data);
    } catch { /* ignore */ }
  }
  async function loadPrefs() {
    try {
      const { data } = await api.get("/notifications/prefs");
      setPrefs(data);
    } catch { /* ignore */ }
  }
  async function toggleMute(cat) {
    const isMuted = prefs.muted.includes(cat);
    const next = isMuted ? prefs.muted.filter((m) => m !== cat) : [...prefs.muted, cat];
    setPrefs({ ...prefs, muted: next });
    try { await api.patch("/notifications/prefs", { muted: next }); }
    catch { loadPrefs(); }
  }
  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false); setShowPrefs(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  async function markAll() {
    await api.post("/notifications/read-all");
    load();
  }
  async function openItem(n) {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  }

  const sevColor = (s) => s === "danger" ? "border-l-rose-500" : s === "warning" ? "border-l-amber-500" : s === "success" ? "border-l-emerald-500" : "border-l-blue-500";

  return (
    <div ref={ref} className="absolute top-3 right-4 lg:top-5 lg:right-8 z-30">
      <button onClick={() => { setOpen((o) => !o); load(); }}
        data-testid="notif-bell-btn"
        className="relative h-10 w-10 flex items-center justify-center bg-white border border-zinc-300 hover:bg-zinc-50">
        <Bell className="w-4 h-4" />
        {data.unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1.5 bg-rose-600 text-white text-[10px] font-mono font-bold rounded-full flex items-center justify-center"
                data-testid="notif-unread-badge">
            {data.unread > 99 ? "99+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 max-h-[70vh] bg-white border border-zinc-200 shadow-lg overflow-hidden flex flex-col"
             data-testid="notif-dropdown">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="font-display font-bold tracking-tight text-sm">
                {showPrefs ? "Notification Preferences" : "Notifications"}
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {showPrefs
                  ? `${prefs.muted.length} muted · ${prefs.available_categories.length} total`
                  : `${data.unread} unread`}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!showPrefs && data.unread > 0 && (
                <button onClick={markAll}
                        className="text-xs font-mono uppercase tracking-wider text-blue-700 hover:underline"
                        data-testid="notif-mark-all-read">
                  mark all read
                </button>
              )}
              <button
                onClick={() => { setShowPrefs((s) => { const next = !s; if (next) loadPrefs(); return next; }); }}
                className="p-1 rounded hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900"
                aria-label="Notification preferences"
                data-testid="notif-prefs-toggle">
                <SlidersHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
          {showPrefs ? (
            <div className="overflow-y-auto flex-1 p-3" data-testid="notif-prefs-panel">
              <div className="text-[11px] text-zinc-500 mb-2 px-1 leading-snug">
                Mute categories you don&apos;t want to be pinged about.
                Escalations &amp; delay reminders stay on by default.
              </div>
              {prefs.available_categories.map((cat) => {
                const muted = prefs.muted.includes(cat);
                return (
                  <button key={cat}
                    onClick={() => toggleMute(cat)}
                    data-testid={`notif-pref-toggle-${cat}`}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-zinc-50 rounded group">
                    <span className="text-sm font-medium text-zinc-800">
                      {cat.replace(/_/g, " ")}
                    </span>
                    <span className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 h-6 rounded-full border
                      ${muted
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                      {muted ? <><BellOff className="w-3 h-3" /> muted</> : <><Bell className="w-3 h-3" /> on</>}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
          <div className="overflow-y-auto flex-1">
            {data.items.length === 0 && (
              <div className="p-6 text-center text-sm text-zinc-400">All caught up</div>
            )}
            {data.items.map((n) => {
              const isSpecific = n.kind === "specific";
              return (
              <button key={n.id} onClick={() => openItem(n)}
                data-testid={`notif-item-${n.id}`}
                className={`block w-full text-left px-4 py-3 border-b border-l-4 ${sevColor(n.severity)}
                  ${n.read ? "bg-white" : isSpecific ? "bg-orange-50" : "bg-blue-50/40"} hover:bg-zinc-50`}>
                {isSpecific && (
                  <div className="inline-flex items-center gap-1 mb-1 px-1.5 h-5 rounded text-[9px] font-mono uppercase tracking-widest bg-orange-600 text-white">
                    Action Required
                  </div>
                )}
                <div className={`text-sm leading-snug ${isSpecific ? "font-semibold" : ""}`}>{n.message}</div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-1 flex gap-2 flex-wrap">
                  <span>{fmtISTNice(n.created_at)}</span>
                  {n.actor_name && <span>· by {n.actor_name}</span>}
                  {n.category && n.category !== "general" && (
                    <span className="px-1.5 bg-zinc-100 text-zinc-600 rounded">{n.category}</span>
                  )}
                </div>
              </button>
              );
            })}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Role-aware home redirect — sends each user to their primary screen.
 */
export function RoleHome() {
  const { user } = useAuth();
  if (!user) return null;
  const map = {
    admin: "/dashboard", management: "/dashboard",
    scope_user: "/projects",
    csm: "/projects", psm: "/projects", ism: "/projects",
    designer: "/my-designs", prepress: "/prepress", production: "/production",
    pre_audit: "/my-preaudit",
    site_inspector: "/site-install",
    accounts: "/accounts-billing",
    sales: "/po-followup",
    procurement_user: "/procurement",
    client: "/my-projects",
  };
  const to = map[user.role] || "/projects";
  // A custom user type may not include the role's default home — fall back to
  // the first page it is allowed to open.
  if (Array.isArray(user.allowed_pages) && user.role !== "admin"
      && !user.allowed_pages.includes(to)) {
    return <RedirectTo to={user.allowed_pages[0] || "/projects"} />;
  }
  return <RedirectTo to={to} />;
}

function RedirectTo({ to }) {
  const navigate = useNavigate();
  if (typeof window !== "undefined") setTimeout(() => navigate(to, { replace: true }), 0);
  return null;
}
